<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          Data Marts
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border" style="padding: 0.5rem">
              <div class="list-content">
                <a role="button" tabindex="0" @keyup.enter="addNewDataMart()"
                  :class="{ disabled: loading || loadingError }" title="Add">
                  <font-awesome-icon :icon="['far', 'plus']" size="lg" title="Add Data Mart" @click="addNewDataMart()" />
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-form style="padding: 1rem; margin-right: 0">
        <template #es-form>
          <es-loader v-if="loadingDestination" class="loader-center" />
          <es-exception v-else-if="loadingError" style="margin: 0; padding: 1rem" exceptionMessage="There was an issue.
            Please try again or contact your administrator." />
          <table v-else style="width: 100%">
            <thead>
              <tr>
                <th></th>
                <th class="rule" title="Destination Mart Name">
                  Destination Mart Name
                </th>
                <th class="rule" title="Status">
                  Status
                </th>
                <th class="rule" title="Last Synch Date">
                  Last Synch Date
                </th>
                <th class="rule" title="Actions">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(datamart, index) in sortedDatamarts" :key="index">
                <td style="width: 0px">
                  <font-awesome-icon v-if="datamart.sourceRecordCount !== datamart.destinationRecordCount"
                    title="Data out of Sync." class="fa-lg has-text-warning" :icon="['fas', 'exclamation-triangle']" />
                </td>
                <td>
                  {{ datamart.label }}
                </td>
                <td>
                  {{ datamart.active === true ? 'Active' : 'Inactive' }}
                </td>
                <td>
                  {{ datamart.syncTime | formatDate }}
                </td>
                <td>
                  <a role="button" tabindex="0" @keyup.enter="reloadDatamart(datamart.id)"
                    title="Data Mart Synchronization Details" @click="reloadDatamart(datamart.id)">
                    <span style="padding-right: 10px; font-size: 13px">
                      <font-awesome-icon :icon="['fas', 'sync-alt']" size="lg"
                        title="Data Mart Synchronization Details" />
                    </span>
                  </a>
                  <a role="button" tabindex="0" @keyup.enter="dataMartDetails(datamart)" title="View Details"
                    @click="dataMartDetails(datamart)">
                    <span style="font-size: 17px">
                      <font-awesome-icon :icon="['far', 'angle-double-right']" size="lg" title="View Details" />
                    </span>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faRecycle,
  faExclamationTriangle,
  faSyncAlt,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import moment from 'moment';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faExclamationTriangle,
  faAngleDoubleRight,
  faPlus,
  faSyncAlt,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  mounted() {
    this.onLoad();
  },
  computed: {
    sortedDatamarts() {
      return this.datamarts.slice().sort(this.compare);
    },
  },
  data() {
    return this.initialData();
  },
  methods: {
    ...mapActions('lookups', [
      'loadDatamartTypes',
    ]),
    compare(a, b) {
      if (a.label.toUpperCase() < b.label.toUpperCase()) {
        return -1;
      }
      if (a.label.toUpperCase() > b.label.toUpperCase()) {
        return 1;
      }
      return 0;
    },
   async dataMartDetails(datamart) {
      this.hideNotification();
      this.$emit('openDetails', datamart);
    },
    addNewDataMart() {
      //this.showNotification('warning', ['Remember to prime the destination table before turning on synchronization.']);
      this.$emit('addDataMart');
    },
    initialData() {
      return {
        datamarts: [],
        destinationDatamarts: [],
        loading: false,
        loadingError: false,
        loadingDestination: false,
      };
    },
    async onLoad() {
      try {
        this.loadingDestination = true;
        this.loadDatamartTypes();
        const res = await axios.get('datamart/destination');
        if (res.status === 200) {
            this.datamarts = res.data;
        }
         this.loadingDestination = false;
         this.loadingError = false;
      } catch (error) {
        this.loadingError = true;
        const apiMsg = error.response ? error.response.data.message : 'There was an issue. Please try again or contact your administrator.';
        this.showNotification('danger', [apiMsg]);
      }
    },
    async reloadDatamart(id) {
      try {
        this.hideNotification();
        const res = await axios.get(`datamart/destination/${id}`);
        const index = this.datamarts.findIndex(datamart => datamart.id === id);
        if (index !== -1) {
          this.datamarts.splice(index, 1, res.data);
          this.showNotification('success', ['The Data Mart Refresh request has been initiated successfully.']);
        }
      } catch (error) {
        this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
      }
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY HH:mm:ss');
      }
      return 'N/A';
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
    &:last-child
      text-align: center
      width: 1%
      white-space: nowrap
  table td
    padding-bottom: 0
    &:last-child
      text-align: center
      width: 1%
      white-space: nowrap
  a.disabled
    color: #CCC
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 2rem
    font-size: 12px
    width: 50%
  .no-border
    border: 0
  .rule
    font-size: 12px
    color: #666
  .content
    padding: 7px 0
  .first-column
    padding-left: 0
    margin-bottom: 0

  /* The switch - the box around the slider */
  .switch
    position: relative
    display: inline-block
    width: 36px
    height: 18px

  /* Hide default HTML checkbox */
  .switch input
    opacity: 0
    width: 0
    height: 0

  /* The slider */
  .slider
    position: absolute
    cursor: pointer
    top: 0
    left: 0
    right: 0
    bottom: 0
    background-color: #ccc
    -webkit-transition: .4s
    transition: .4s

  .slider:before
    position: absolute
    content: ""
    height: 16px
    width: 16px
    left: 2px
    top: 1px
    bottom: 1px
    background-color: white
    -webkit-transition: .4s
    transition: .4s

  /*Change background color on selection*/
  input:checked + .slider
    background-color: #5953a4

  input:focus + .slider
    box-shadow: 0 0 1px #2196F3

  input:checked + .slider:before
    -webkit-transform: translateX(16px)
    -ms-transform: translateX(16px)
    transform: translateX(16px)

  /* Rounded slider box */
  .slider.round
    border-radius: 16px

  /* Rounded slider*/
  .slider.round:before
    border-radius: 50%

  .disableSlider
    input:checked + .slider
      background-color: #ccc
  .content
    overflow-x: auto
</style>
